import React, {useCallback, useEffect, useRef, useState} from "react";
import "nodeEditor/nodeEditor.css";
import createEditor from "nodeEditor/createEditor";
import {useGetNodeFileList} from "hooks/useGetNodeFileList";
import {useGetNodeFileGroups} from "hooks/useGetNodeVideoGroups";
import { EditorContainer } from './NodeEditor.styles';

interface NodeEditorProps {
    onEditorChange: (editor: object) => void;
    config?: object | null;
    lessonId?: string,
}

/**
 * @requires key - Must provide a unique key prop that changes with context (e.g., `${lessonId}-${language}`)
 */
export const NodeEditor = (props: NodeEditorProps) => {
    const {onEditorChange, config, lessonId} = props;
    const {fetchNodeFileList} = useGetNodeFileList();
    const {fetchNodeFileGroups} = useGetNodeFileGroups();
    const [editor, setEditor] = useState<any>(null);
    const editorRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const handleEditorLoaded = useCallback((editor: any) => {
        setEditor(editor);
        editorRef.current = editor;
    }, []);

    const handleEditorChange = useCallback(() => {
        if (!editorRef.current) return;
        onEditorChange(editorRef.current);
    }, [onEditorChange]);

    useEffect(() => {
        if (!containerRef.current) return;

        // cleanup old editor
        if (editorRef.current) {
            try {
                editorRef.current.trigger('destroy');
                if (editorRef.current.clear) {
                    editorRef.current.clear();
                }
            } catch (e) {
                console.error('Error destroying editor:', e);
            }

            editorRef.current = null;
            setEditor(null);
        }

        containerRef.current.innerHTML = "";

        createEditor(
            containerRef.current,
            config,
            handleEditorChange,
            handleEditorLoaded,
            fetchNodeFileList,
            fetchNodeFileGroups,
            lessonId
        );

        return () => {
            if (editorRef.current) {
                try {
                    editorRef.current.trigger('destroy');
                    if (editorRef.current.clear) {
                        editorRef.current.clear();
                    }
                } catch (e) {
                    console.error('Error destroying editor on unmount:', e);
                }
            }
        };
    }, [config, handleEditorChange, handleEditorLoaded, fetchNodeFileList, fetchNodeFileGroups, lessonId]);

    useEffect(() => {
        handleEditorChange();
    }, [editor, handleEditorChange]);

    return (
        <EditorContainer ref={containerRef} className="rete" id="rete"></EditorContainer>
    );
}